import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { api } from '../services/api';
import { NEUTRAL, SEVERITY_COLORS } from '../theme';

const ENV_PREFIX = { prod: 'Prod', staging: 'Staging', dev: 'Dev' };
const ENV_LABELS = { prod: 'Production', staging: 'Staging', dev: 'Development' };
const ENV_WIDE = '__env__';

// Recommended alerts per category. Thresholds/durations are sent explicitly so the
// summary shown to the user always matches what gets created.
// envPolicyName is the name (without env prefix) of the equivalent environment-wide
// policy provisioned by Terraform in monitoring/*.tf; keep them in sync.
export const ALERT_BUNDLES = [
  {
    id: 'connectivity',
    title: 'Connectivity',
    description: 'Network devices going offline, packet loss and unstable connections.',
    icon: RouterOutlinedIcon,
    alerts: [
      {
        component: 'network_manager',
        templateId: 'high_offline_percentage',
        envPolicyName: 'Network Manager High Offline Device Percentage',
        name: 'High Offline Device Percentage',
        summary: 'More than 10% of network devices offline for 5 minutes',
        severity: 'warning',
        threshold: 10,
        duration: '300s',
      },
      {
        component: 'network_manager',
        templateId: 'high_packet_loss',
        envPolicyName: 'Network Manager High Packet Loss',
        name: 'High Packet Loss',
        summary: 'Average packet loss above 5% for 5 minutes',
        severity: 'critical',
        threshold: 5,
        duration: '300s',
      },
      {
        component: 'network_manager',
        templateId: 'low_connected_time',
        envPolicyName: 'Network Manager Low Average Connected Time',
        name: 'Low Average Connected Time',
        summary: 'Devices stay connected less than 1 hour on average, for 10 minutes',
        severity: 'warning',
        threshold: 3600,
        duration: '600s',
      },
    ],
  },
  {
    id: 'lighting',
    title: 'Lighting',
    description: 'Lamps offline, gateways down and devices that stop reporting.',
    icon: LightbulbOutlinedIcon,
    alerts: [
      {
        component: 'lms',
        templateId: 'high_offline_lamps_percentage',
        envPolicyName: 'LMS High Offline Lamps Percentage',
        name: 'High Offline Lamps Percentage',
        summary: 'More than 10% of lamps offline for 5 minutes',
        severity: 'warning',
        threshold: 10,
        duration: '300s',
      },
      {
        component: 'lms',
        templateId: 'low_active_gateways_percentage',
        envPolicyName: 'LMS Low Active Gateways Percentage',
        name: 'Low Active Gateways Percentage',
        summary: 'Fewer than 80% of gateways connected for 5 minutes',
        severity: 'critical',
        threshold: 80,
        duration: '300s',
      },
      {
        component: 'lms',
        templateId: 'stale_last_activity',
        envPolicyName: 'LMS Stale Device Activity',
        name: 'Stale Device Activity',
        summary: 'No device activity for more than 15 minutes',
        severity: 'warning',
        threshold: 900,
        duration: '600s',
      },
    ],
  },
  {
    id: 'data',
    title: 'Data',
    description: 'Failed pipeline jobs, unprocessed messages, stalled detections and unstable edge pipelines.',
    icon: StorageOutlinedIcon,
    alerts: [
      {
        component: 'dataflow',
        templateId: 'job_failure',
        envPolicyName: 'Dataflow Job Failure Alert',
        name: 'Dataflow Job Failure',
        summary: 'Any Dataflow job fails',
        severity: 'critical',
        threshold: 0,
        duration: '60s',
      },
      {
        component: 'dataform',
        templateId: 'job_failure',
        envPolicyName: 'Dataform Job Failure Alert',
        name: 'Dataform Job Failure',
        summary: 'Any Dataform job fails',
        severity: 'critical',
        threshold: 0,
        duration: '60s',
      },
      {
        component: 'pubsub',
        templateId: 'dlq_messages',
        envPolicyName: 'Pubsub Dead Letter Queue (DLQ) Messages',
        name: 'Pub/Sub Dead Letter Messages',
        summary: 'More than ~6 dead-letter messages per minute for 10 minutes',
        severity: 'critical',
        threshold: 0.1,
        duration: '600s',
      },
      {
        component: 'pubsub',
        templateId: 'no_messages_detections_dataflow',
        envPolicyName: 'Pub/Sub No Messages in detections_dataflow (12h)',
        name: 'Pub/Sub No Messages in detections_dataflow (12h)',
        summary: 'No detection messages processed in the last 12 hours',
        severity: 'critical',
        threshold: 0,
        duration: '0s',
      },
      {
        component: 'edge_devices',
        templateId: 'pipeline_restart_count',
        envPolicyName: 'Edge Devices Pipeline Restart Count',
        name: 'Edge Devices Pipeline Restart Count',
        summary: 'A device pipeline restarts more than 10 times in an hour',
        severity: 'warning',
        threshold: 10,
        duration: '3600s',
        evaluationInterval: '3600s',
      },
    ],
  },
];

const buildDisplayName = (env, alertName, scope) => {
  const prefix = ENV_PREFIX[env] || 'Custom';
  return scope === ENV_WIDE ? `${prefix} ${alertName}` : `${prefix} ${alertName} (${scope})`;
};

// Names an equivalent policy may have in the given scope (ours, plus the Terraform name env-wide).
const candidateNames = (env, alert, scope) => {
  const prefix = ENV_PREFIX[env] || 'Custom';
  const names = [buildDisplayName(env, alert.name, scope), `${prefix} ${alert.name}`];
  if (scope === ENV_WIDE && alert.envPolicyName) {
    names.push(`${prefix} ${alert.envPolicyName}`);
  }
  return new Set(names.map((n) => n.toLowerCase()));
};

const preferEnabled = (list) => list.find((p) => p.enabled) || list[0] || null;

const subscribedEmails = (policy) =>
  new Set((policy?.subscribers || []).map((sub) => String(sub?.email || '').toLowerCase()).filter(Boolean));

const pluralize = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const CategoryCard = ({ bundle, selected, onSelect }) => {
  const Icon = bundle.icon;
  return (
    <ButtonBase
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        gap: 1,
        p: 2,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: selected ? NEUTRAL[900] : NEUTRAL[200],
        boxShadow: selected ? `inset 0 0 0 1px ${NEUTRAL[900]}` : 'none',
        bgcolor: 'background.paper',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': { borderColor: selected ? NEUTRAL[900] : NEUTRAL[300], bgcolor: NEUTRAL[50] },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: NEUTRAL[100],
          color: NEUTRAL[700],
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant="body2" fontWeight={600}>{bundle.title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {bundle.description}
      </Typography>
    </ButtonBase>
  );
};

const StepLabel = ({ index, children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
    <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>{index}.</Box>
    {children}
  </Typography>
);

const SimplePolicySetup = ({
  env,
  isEnvUser,
  projects,
  policyTemplates,
  templatesLoading,
  policies,
  policiesReady,
  policiesError,
  getPolicyOwner,
  getPolicyEnv,
  emailChannels,
  channelsLoading,
  onCancel,
  onBusyChange,
  onChanged,
  onDone,
}) => {
  const [bundleIds, setBundleIds] = useState(() => new Set());
  const [scopes, setScopes] = useState(() => (
    isEnvUser ? [ENV_WIDE] : (projects[0] ? [projects[0]] : [])
  ));
  const [channels, setChannels] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [error, setError] = useState(null);
  // Guards against re-creating policies before the refreshed list comes back.
  const [createdKeys, setCreatedKeys] = useState(() => new Set());

  const selectedAlerts = useMemo(
    () => ALERT_BUNDLES.filter((b) => bundleIds.has(b.id)).flatMap((b) => b.alerts),
    [bundleIds]
  );
  const envLabel = ENV_LABELS[env] || env;

  const findMatches = useCallback(
    (alert, matchScope) => {
      const names = candidateNames(env, alert, matchScope);
      return policies.filter((p) => {
        if (!names.has(String(p.displayName || '').toLowerCase())) return false;
        if (getPolicyEnv(p) !== env) return false;
        const component = p.userLabels?.component;
        if (component && component !== alert.component) return false;
        const owner = getPolicyOwner(p);
        return matchScope === ENV_WIDE
          ? owner === 'system'
          : owner === 'project' && p.userLabels?.project_id === matchScope;
      });
    },
    [env, policies, getPolicyEnv, getPolicyOwner]
  );

  const wantedEmails = useMemo(
    () => channels.map((c) => String(c.email || '').toLowerCase()).filter(Boolean),
    [channels]
  );

  const plan = useMemo(() => {
    if (selectedAlerts.length === 0 || scopes.length === 0) return [];
    return scopes.flatMap((scope) => selectedAlerts.map((a) => {
      const key = `${scope}|${a.component}:${a.templateId}`;
      const displayName = buildDisplayName(env, a.name, scope);
      const available = (policyTemplates[a.component] || []).some((t) => t.id === a.templateId);
      const existing = preferEnabled(findMatches(a, scope));
      const covering = scope === ENV_WIDE ? null : preferEnabled(findMatches(a, ENV_WIDE));
      let status;
      if (existing) status = existing.enabled ? 'active' : 'disabled';
      else if (createdKeys.has(key)) status = 'created';
      else if (covering?.enabled) status = 'covered';
      else status = available ? 'new' : 'unavailable';
      const have = subscribedEmails(existing);
      const missingEmails = existing ? wantedEmails.filter((e) => !have.has(e)) : [];
      return { ...a, scope, key, displayName, existing, covering, status, missingEmails };
    }));
  }, [selectedAlerts, scopes, env, policyTemplates, findMatches, createdKeys, wantedEmails]);

  const toCreate = plan.filter((a) => a.status === 'new');
  const toEnable = plan.filter((a) => a.status === 'disabled');
  const toSubscribe = plan.filter((a) => a.status === 'active' && a.missingEmails.length > 0);
  const actionCount = toCreate.length + toEnable.length + toSubscribe.length;

  const policyRef = (policy) => ({
    id: policy.name.split('/').pop(),
    isProject: getPolicyOwner(policy) === 'project',
  });

  const handleCreate = async () => {
    setSubmitting(true);
    onBusyChange(true);
    setError(null);
    const failures = [];
    const changes = { created: [], enabled: [], subscribed: [] };
    const newlyCreated = [];

    const addMissingEmails = async (a) => {
      const { id, isProject } = policyRef(a.existing);
      for (const email of a.missingEmails) {
        try {
          if (isProject) await api.subscribeToProjectPolicy(id, email);
          else await api.subscribeToPolicy(id, email);
          changes.subscribed.push({ policyName: a.existing.name, email });
        } catch (err) {
          failures.push(`${a.name}: could not add ${email} (${err.message || 'failed'})`);
        }
      }
    };

    for (const a of toEnable) {
      const { id, isProject } = policyRef(a.existing);
      try {
        if (isProject) await api.updateProjectPolicyEnabled(id, true);
        else await api.updatePolicyEnabled(id, true);
        changes.enabled.push(a.existing.name);
      } catch (err) {
        failures.push(`${a.name}: could not turn on (${err.message || 'failed'})`);
        continue;
      }
      await addMissingEmails(a);
    }
    for (const a of toSubscribe) {
      await addMissingEmails(a);
    }

    const createPayload = (a) => ({
      clientKey: a.key,
      component: a.component,
      templateId: a.templateId,
      severity: a.severity,
      displayName: a.displayName,
      threshold: a.threshold,
      duration: a.duration,
      evaluationInterval: a.evaluationInterval || '60s',
      autoClose: 7 * 24 * 3600,
      env: env || undefined,
      owner: a.scope === ENV_WIDE ? 'system' : 'project',
      projectId: a.scope === ENV_WIDE ? undefined : a.scope,
      enabled: true,
      notificationChannels: channels.map((c) => c.name),
    });

    const recordCreated = (a, policy) => {
      newlyCreated.push(a.key);
      if (policy) {
        changes.created.push({
          ...policy,
          subscribers: channels
            .filter((c) => c.email)
            .map((c) => ({ email: c.email, channelName: c.name, displayName: c.email, type: 'email' })),
        });
      }
    };

    const createSequentially = async (items) => {
      for (const a of items) {
        try {
          const result = await api.createPolicy(createPayload(a));
          recordCreated(a, result?.policy);
        } catch (err) {
          failures.push(`${a.name}: ${err.message || 'failed to create'}`);
        }
      }
    };

    const byKey = new Map(toCreate.map((a) => [a.key, a]));
    const applyBatchResults = (results) => {
      (results || []).forEach((result) => {
        const planned = byKey.get(result.clientKey);
        if (result.status === 'created') recordCreated(planned || { key: result.clientKey }, result.policy);
        else failures.push(`${planned?.name || 'Policy'}: ${result.error || 'failed to create'}`);
      });
    };

    if (toCreate.length > 1) {
      let job = null;
      try {
        job = await api.createPoliciesBatch(toCreate.map(createPayload));
      } catch (err) {
        if (err.statusCode === 404) {
          await createSequentially(toCreate);
        } else if (err.results?.length) {
          applyBatchResults(err.results);
        } else {
          failures.push(err.message || 'Failed to create policies');
        }
      }

      if (job) {
        setBatchProgress(job);
        const pollDeadline = Date.now() + 10 * 60 * 1000;
        let pollErrors = 0;
        let lostTrack = false;
        while (!job.done && (job.status === 'queued' || job.status === 'running')) {
          if (Date.now() > pollDeadline) {
            lostTrack = true;
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 1500));
          try {
            job = await api.getPolicyBatch(job.jobId);
            setBatchProgress(job);
            pollErrors = 0;
          } catch (err) {
            pollErrors += 1;
            if (pollErrors >= 5) {
              lostTrack = true;
              break;
            }
          }
        }
        applyBatchResults(job.results);
        if (lostTrack) {
          const unknown = job.total - (job.results || []).length;
          failures.push(
            `Lost track of ${unknown} of ${job.total} policies. Refresh Policies to see which were created before retrying`
          );
        }
      }
    } else {
      await createSequentially(toCreate);
    }

    if (newlyCreated.length > 0) {
      setCreatedKeys((prev) => new Set([...prev, ...newlyCreated]));
    }
    setSubmitting(false);
    setBatchProgress(null);
    onBusyChange(false);
    const changed = changes.created.length + changes.enabled.length + changes.subscribed.length + newlyCreated.length;
    if (changed > 0) onChanged(changes);
    if (failures.length > 0) {
      setError(`Some steps failed: ${failures.join('; ')}`);
      return;
    }
    onDone();
  };

  const noScopes = !isEnvUser && projects.length === 0;

  return (
    <>
      <DialogContent dividers>
        {templatesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 0.5 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
            )}
            {batchProgress && (
              <Alert severity="info">
                Creating {batchProgress.completed} of {batchProgress.total} policies. Keep this window open
                until it finishes; closing or reloading the page stops the remaining ones.
              </Alert>
            )}
            {!policiesReady && (
              <Alert severity={policiesError ? 'error' : 'info'}>
                {policiesError
                  ? 'Existing policies could not be loaded, so duplicates cannot be checked. Refresh the Policies tab and try again.'
                  : 'Checking existing policies…'}
              </Alert>
            )}

            <Box>
              <StepLabel index={1}>What do you want to be alerted about?</StepLabel>
              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                {ALERT_BUNDLES.map((b) => (
                  <CategoryCard
                    key={b.id}
                    bundle={b}
                    selected={bundleIds.has(b.id)}
                    onSelect={() => setBundleIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(b.id)) next.delete(b.id);
                      else next.add(b.id);
                      return next;
                    })}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Select one or more.
              </Typography>
            </Box>

            {bundleIds.size > 0 && (
              <Box>
                <StepLabel index={2}>Environment</StepLabel>
                {noScopes ? (
                  <Alert severity="warning">You don't have access to any project in {envLabel}.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        multiple
                        displayEmpty
                        value={scopes}
                        onChange={(e) => {
                          const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                          const addedEnvWide = value.includes(ENV_WIDE) && !scopes.includes(ENV_WIDE);
                          setScopes(addedEnvWide ? [ENV_WIDE] : value.filter((id) => id !== ENV_WIDE));
                        }}
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return <Box component="span" sx={{ color: 'text.secondary' }}>Select projects</Box>;
                          }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((id) => (
                                <Chip
                                  key={id}
                                  size="small"
                                  label={id === ENV_WIDE ? `All projects in ${envLabel}` : id}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onDelete={() => setScopes((prev) => prev.filter((item) => item !== id))}
                                />
                              ))}
                            </Box>
                          );
                        }}
                      >
                        {isEnvUser && (
                          <MenuItem value={ENV_WIDE}>All projects in {envLabel}</MenuItem>
                        )}
                        {projects.map((pid) => (
                          <MenuItem key={pid} value={pid}>{pid}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      disabled={scopes.length === 0}
                      onClick={() => setScopes([])}
                      sx={{ flexShrink: 0, mt: 0.25 }}
                    >
                      Clear
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {bundleIds.size > 0 && scopes.length > 0 && (
              <Box>
                <StepLabel index={3}>Email me too (optional)</StepLabel>
                <Autocomplete
                  multiple
                  size="small"
                  loading={channelsLoading}
                  options={emailChannels}
                  value={channels}
                  onChange={(_e, v) => setChannels(v)}
                  isOptionEqualToValue={(o, v) => o.name === v.name}
                  getOptionLabel={(o) => o.email || o.displayName || o.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={channels.length ? '' : 'Choose email recipients'}
                      helperText="Alerts always appear in the Alerts tab. Add emails to also get notified."
                    />
                  )}
                />
              </Box>
            )}

            {plan.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  We'll set up these alerts
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${NEUTRAL[200]}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                  }}
                >
                  {plan.map((a, i) => {
                    const sev = SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.info;
                    const skipped =
                      (a.status === 'active' && a.missingEmails.length === 0) ||
                      ['covered', 'created', 'unavailable'].includes(a.status);
                    return (
                      <Box
                        key={a.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.25,
                          borderTop: i === 0 ? 'none' : `1px solid ${NEUTRAL[200]}`,
                          opacity: skipped ? 0.6 : 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500}>{a.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {scopes.length > 1
                              ? `${a.scope === ENV_WIDE ? `All projects in ${envLabel}` : a.scope} · ${a.summary}`
                              : a.summary}
                          </Typography>
                        </Box>
                        {a.status === 'active' && a.missingEmails.length > 0 ? (
                          <Tooltip title={`Adds ${a.missingEmails.join(', ')} to "${a.existing.displayName}".`}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              Active · add {pluralize(a.missingEmails.length, 'email')}
                            </Typography>
                          </Tooltip>
                        ) : a.status === 'active' || a.status === 'created' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption">{a.status === 'created' ? 'Created' : 'Already active'}</Typography>
                          </Box>
                        ) : a.status === 'covered' ? (
                          <Tooltip title={`"${a.covering.displayName}" already covers every project in this environment.`}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              Covered by environment policy
                            </Typography>
                          </Tooltip>
                        ) : a.status === 'disabled' ? (
                          <Tooltip title={`Turns the existing policy "${a.existing.displayName}" back on instead of creating a duplicate.`}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              Disabled · will turn on
                            </Typography>
                          </Tooltip>
                        ) : a.status === 'unavailable' ? (
                          <Typography variant="caption" color="text.secondary">Not available</Typography>
                        ) : (
                          <Chip
                            size="small"
                            label={a.severity}
                            sx={{ bgcolor: sev.bg, color: sev.color, fontWeight: 500, textTransform: 'capitalize' }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
                {wantedEmails.length > 0 && plan.some((a) => a.status === 'covered') && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Alerts covered by an environment policy keep their existing recipients. To get those emails,
                    subscribe on the environment policy in the Policies tab.
                  </Alert>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Incidents close automatically after 7 days. Switch to Advanced to fine-tune.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={submitting || templatesLoading || !policiesReady || actionCount === 0}
          id="tour-create-submit"
        >
          {submitting ? (
            batchProgress
              ? `Creating ${batchProgress.completed} of ${batchProgress.total}`
              : <CircularProgress size={20} />
          ) : 'Set up alerts'}
        </Button>
      </DialogActions>
    </>
  );
};

export default SimplePolicySetup;
